import React, { useState, useEffect, useCallback } from 'react';
import {
  Download,
  Filter,
  Calendar,
  RefreshCw,
  Clock,
  Zap,
  Target,
  Shield,
  AlertTriangle,
  Brain,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';
import Button from '../ui/button';
import Badge from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';


const EvaluationReportDashboard = ({ evaluationId }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedReportType, setSelectedReportType] = useState('comprehensive');
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    accuracy: true,
    latency: true,
    security: true,
    content: true,
    reliability: true
  });

  // Color schemes for charts
  const METRIC_COLORS = {
    accuracy: '#10B981',
    latency: '#3B82F6',
    security: '#F59E0B',
    offense: '#EF4444',
    hallucination: '#8B5CF6'
  };

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await axios.get(`/api/evaluations/${evaluationId}/reports`, {
      //   params: { timeRange: selectedTimeRange, type: selectedReportType }
      // });
      // setReports(response.data);

      // Mock data for development
      const mockReports = [
        {
          id: 1,
          evaluation_id: evaluationId,
          report_type: 'comprehensive',
          overall_score: 87.5,
          status: 'generated',
          generated_at: '2025-02-08T10:30:00Z',
          summary: 'Evaluation completed with 95 responses. Overall accuracy: 87.5%, Average response time: 2.3s. Good performance with room for improvement.',
          metrics: {
            accuracy: {
              score: 87.5,
              total_responses: 95,
              accurate_responses: 83,
              accuracy_rate: 87.37,
              high_quality_responses: 72,
              high_quality_rate: 75.79,
              standard_deviation: 0.15,
              confidence_intervals: {
                '95%': { lower: 0.82, upper: 0.93 }
              },
              distribution: {
                'excellent': 45,
                'good': 27,
                'fair': 15,
                'poor': 6,
                'very_poor': 2
              },
              quality_breakdown: {
                'excellent': 45,
                'good': 27,
                'fair': 15,
                'poor': 6,
                'very_poor': 2
              },
              consistency_score: 85.2
            },
            latency: {
              average_ms: 2300,
              min_ms: 800,
              max_ms: 5200,
              median_ms: 2100,
              p95_ms: 4200,
              p99_ms: 4800,
              standard_deviation: 850,
              performance_grade: 'B',
              outliers: [
                { index: 12, value: 5200, deviation: 2900 },
                { index: 34, value: 5100, deviation: 2800 }
              ],
              latency_distribution: {
                '0-1s': 8,
                '1-2s': 32,
                '2-3s': 35,
                '3-5s': 18,
                '5s+': 2
              },
              sla_compliance: {
                '1s': { compliant_requests: 8, compliance_rate: 8.42 },
                '2s': { compliant_requests: 40, compliance_rate: 42.11 },
                '3s': { compliant_requests: 75, compliance_rate: 78.95 },
                '5s': { compliant_requests: 93, compliance_rate: 97.89 }
              }
            },
            security: {
              score: 92.3,
              vulnerabilities_found: 2,
              security_issues: [
                {
                  type: 'high_error_rate',
                  severity: 'medium',
                  description: 'High error rate detected, potential security concern',
                  result_id: 45
                }
              ],
              safe_responses: 93,
              risk_level: 'LOW',
              security_categories: {
                input_validation: 95.2,
                output_safety: 91.8,
                data_leakage_prevention: 98.5,
                injection_resistance: 88.7
              },
              compliance_score: 94.1
            },
            offense: {
              offensive_content_detected: 3,
              clean_responses: 92,
              offense_rate: 3.16,
              offense_categories: {
                hate_speech: 0,
                violence: 1,
                harassment: 0,
                discrimination: 0,
                explicit_content: 2,
                illegal_activity: 0,
                misinformation: 0
              },
              severity_breakdown: {
                critical: 0,
                high: 0,
                medium: 2,
                low: 1
              },
              content_safety_score: 96.84,
              false_positive_rate: 2.1,
              detection_accuracy: 94.7
            },
            hallucination: {
              hallucinations_detected: 8,
              factual_responses: 87,
              hallucination_rate: 8.42,
              confidence_scores: {
                average: 0.847,
                min: 0.234,
                max: 0.987,
                standard_deviation: 0.156,
                distribution: {
                  very_high: 42,
                  high: 28,
                  medium: 18,
                  low: 5,
                  very_low: 2
                }
              },
              factual_accuracy_score: 91.58,
              hallucination_types: {
                factual_errors: 3,
                inconsistencies: 2,
                fabricated_information: 2,
                outdated_information: 1,
                context_confusion: 0
              },
              fact_checking_score: 89.3
            },
            summary: {
              total_responses: 95,
              successful_responses: 87,
              failed_responses: 8,
              average_tokens: 425,
              total_tokens: 40375
            }
          },
          recommendations: {
            accuracy: [
              'Consider refining prompts to improve response accuracy',
              'Review training data quality and relevance'
            ],
            performance: [
              'Optimize prompts to reduce response time',
              'Consider using faster model variants'
            ],
            security: [
              'Address identified security vulnerabilities'
            ],
            content: [
              'Review content filtering mechanisms'
            ],
            reliability: [
              'Implement fact-checking mechanisms',
              'Add confidence thresholds for responses'
            ]
          }
        }
      ];

      // Removed reportsmockReports);
      if (mockReports.length > 0) {
        setSelectedReport(mockReports[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load reports:', error);
      setLoading(false);
    }
  }, [evaluationId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleExportReport = async (format = 'pdf') => {
    if (!selectedReport) return;

    try {
      // TODO: Replace with actual API call
      // const response = await axios.get(`/api/reports/${selectedReport.id}/export`, {
      //   params: { format },
      //   responseType: 'blob'
      // });

      // Mock implementation
      const reportData = {
        id: selectedReport.id,
        evaluation_id: selectedReport.evaluation_id,
        generated_at: selectedReport.generated_at,
        overall_score: selectedReport.overall_score,
        summary: selectedReport.summary,
        metrics: selectedReport.metrics
      };

      const jsonContent = JSON.stringify(reportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluation-report-${selectedReport.id}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Loading evaluation reports...</p>
      </div>
    );
  }

  if (!selectedReport) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Reports Available</h3>
        <p className="text-muted-foreground">
          No evaluation reports found for the selected criteria.
        </p>
      </div>
    );
  }

  // Prepare chart data
  const overallMetricsData = [
    { name: 'Accuracy', value: selectedReport.metrics.accuracy.score, color: METRIC_COLORS.accuracy },
    { name: 'Security', value: selectedReport.metrics.security.score, color: METRIC_COLORS.security },
    { name: 'Content Safety', value: selectedReport.metrics.offense.content_safety_score, color: METRIC_COLORS.offense },
    { name: 'Factual Accuracy', value: selectedReport.metrics.hallucination.factual_accuracy_score, color: METRIC_COLORS.hallucination }
  ];

  const accuracyDistributionData = Object.entries(selectedReport.metrics.accuracy.distribution).map(([key, value]) => ({
    name: key.replace('_', ' ').toUpperCase(),
    value: value
  }));

  const latencyDistributionData = Object.entries(selectedReport.metrics.latency.latency_distribution).map(([key, value]) => ({
    name: key,
    value: value
  }));

  const securityCategoriesData = Object.entries(selectedReport.metrics.security.security_categories).map(([key, value]) => ({
    category: key.replace('_', ' ').toUpperCase(),
    score: value
  }));

  const offenseCategoriesData = Object.entries(selectedReport.metrics.offense.offense_categories)
    .filter(([key, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.replace('_', ' ').toUpperCase(),
      value: value
    }));

  const hallucinationTypesData = Object.entries(selectedReport.metrics.hallucination.hallucination_types)
    .filter(([key, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.replace('_', ' ').toUpperCase(),
      value: value
    }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Evaluation Report Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive analysis of evaluation performance and quality metrics
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={loadReports}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => handleExportReport('pdf')}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-secondary/20 rounded-lg">
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={selectedReportType}
            onChange={(e) => setSelectedReportType(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
          >
            <option value="comprehensive">Comprehensive</option>
            <option value="summary">Summary</option>
            <option value="detailed">Detailed</option>
          </select>
        </div>
      </div>

      {/* Executive Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Executive Summary</span>
            </CardTitle>
            <Badge variant={selectedReport.overall_score >= 80 ? "default" : "destructive"}>
              Overall Score: {selectedReport.overall_score}%
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <Target className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{selectedReport.metrics.accuracy.score}%</p>
              <p className="text-sm text-muted-foreground">Accuracy</p>
            </div>
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{selectedReport.metrics.latency.average_ms}ms</p>
              <p className="text-sm text-muted-foreground">Avg Response Time</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <Shield className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{selectedReport.metrics.security.score}%</p>
              <p className="text-sm text-muted-foreground">Security Score</p>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <Brain className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{selectedReport.metrics.hallucination.factual_accuracy_score}%</p>
              <p className="text-sm text-muted-foreground">Factual Accuracy</p>
            </div>
          </div>

          <div className="bg-secondary/50 p-4 rounded-lg">
            <p className="text-foreground">{selectedReport.summary}</p>
          </div>
        </CardContent>
      </Card>

      {/* Overall Performance Radar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <RadarChart className="w-5 h-5" />
            <span>Performance Overview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80">
              {overallMetricsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={overallMetricsData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="name" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Score"
                      dataKey="value"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.3}
                    />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No metrics data available
                </div>
              )}
            </div>
            <div className="h-80">
              {overallMetricsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={overallMetricsData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {overallMetricsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No metrics data available
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Accuracy Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Target className="w-5 h-5" />
              <span>Accuracy Analysis</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleSection('accuracy')}
            >
              {expandedSections.accuracy ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.accuracy && (
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-4">Quality Distribution</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={accuracyDistributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4">Key Metrics</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Responses</span>
                    <span className="font-medium">{selectedReport.metrics.accuracy.total_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Accurate Responses</span>
                    <span className="font-medium">{selectedReport.metrics.accuracy.accurate_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">High Quality Rate</span>
                    <span className="font-medium">{selectedReport.metrics.accuracy.high_quality_rate}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Consistency Score</span>
                    <span className="font-medium">{selectedReport.metrics.accuracy.consistency_score}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Standard Deviation</span>
                    <span className="font-medium">{selectedReport.metrics.accuracy.standard_deviation}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Latency Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>Latency Analysis</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleSection('latency')}
            >
              {expandedSections.latency ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.latency && (
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-4">Response Time Distribution</h4>
                <div className="h-64">
                  {latencyDistributionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={latencyDistributionData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      No latency data available
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4">Performance Metrics</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Average Latency</span>
                    <span className="font-medium">{selectedReport.metrics.latency.average_ms}ms</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Median Latency</span>
                    <span className="font-medium">{selectedReport.metrics.latency.median_ms}ms</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">95th Percentile</span>
                    <span className="font-medium">{selectedReport.metrics.latency.p95_ms}ms</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Performance Grade</span>
                    <Badge variant={selectedReport.metrics.latency.performance_grade === 'A' ? 'default' : 'secondary'}>
                      {selectedReport.metrics.latency.performance_grade}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">SLA Compliance (3s)</span>
                    <span className="font-medium">{selectedReport.metrics.latency.sla_compliance['3s'].compliance_rate}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Security Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Security Analysis</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleSection('security')}
            >
              {expandedSections.security ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.security && (
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-4">Security Categories</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={securityCategoriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Bar dataKey="score" fill="#F59E0B" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4">Security Overview</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Overall Security Score</span>
                    <span className="font-medium">{selectedReport.metrics.security.score}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Risk Level</span>
                    <Badge variant={selectedReport.metrics.security.risk_level === 'LOW' ? 'default' : 'destructive'}>
                      {selectedReport.metrics.security.risk_level}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Vulnerabilities Found</span>
                    <span className="font-medium">{selectedReport.metrics.security.vulnerabilities_found}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Safe Responses</span>
                    <span className="font-medium">{selectedReport.metrics.security.safe_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Compliance Score</span>
                    <span className="font-medium">{selectedReport.metrics.security.compliance_score}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Content Safety Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span>Content Safety Analysis</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleSection('content')}
            >
              {expandedSections.content ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.content && (
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-4">Offense Categories</h4>
                {offenseCategoriesData.length > 0 ? (
                  <div className="h-64">
                    {offenseCategoriesData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={offenseCategoriesData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#EF4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No offense data available
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 dark:text-green-300 font-medium">No Offensive Content Detected</p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4">Content Safety Metrics</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Content Safety Score</span>
                    <span className="font-medium">{selectedReport.metrics.offense.content_safety_score}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Offensive Content Detected</span>
                    <span className="font-medium">{selectedReport.metrics.offense.offensive_content_detected}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Clean Responses</span>
                    <span className="font-medium">{selectedReport.metrics.offense.clean_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Offense Rate</span>
                    <span className="font-medium">{selectedReport.metrics.offense.offense_rate}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Detection Accuracy</span>
                    <span className="font-medium">{selectedReport.metrics.offense.detection_accuracy}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Reliability Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Brain className="w-5 h-5" />
              <span>Reliability Analysis</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleSection('reliability')}
            >
              {expandedSections.reliability ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSections.reliability && (
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-4">Hallucination Types</h4>
                {hallucinationTypesData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hallucinationTypesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#8B5CF6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 dark:text-green-300 font-medium">No Hallucinations Detected</p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4">Reliability Metrics</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Factual Accuracy Score</span>
                    <span className="font-medium">{selectedReport.metrics.hallucination.factual_accuracy_score}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Hallucinations Detected</span>
                    <span className="font-medium">{selectedReport.metrics.hallucination.hallucinations_detected}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Factual Responses</span>
                    <span className="font-medium">{selectedReport.metrics.hallucination.factual_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Hallucination Rate</span>
                    <span className="font-medium">{selectedReport.metrics.hallucination.hallucination_rate}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Fact Checking Score</span>
                    <span className="font-medium">{selectedReport.metrics.hallucination.fact_checking_score}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Zap className="w-5 h-5" />
            <span>Recommendations</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(selectedReport.recommendations).map(([category, items]) => (
              <div key={category} className="space-y-3">
                <h4 className="text-lg font-medium capitalize">{category}</h4>
                <ul className="space-y-2">
                  {items.map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EvaluationReportDashboard;
